import { useQuery } from 'react-query';
import { usePublicApiClient } from '../usePublicApiClient';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { ContentfulCollection } from '@jbhi-fi/apis/src/contentful/types';
import { useContentfulCollections } from '../useContentfulCollection/useContentfulCollection';

export type UseAttachSuggestionProps = {
  purchasedCategoryId?: number;
  contentfulThemeApiClient?: ContentfulThemeApiClient;
  isAttachSuggestionEnabled?: boolean;
};

export type UseAttachSuggestionResult = {
  isLoading: boolean;
  attachHeading?: string;
  productBlockHeading?: string;
  suggestedCategories?: Array<ContentfulCollection>;
};

export const useAttachSuggestion = ({
  purchasedCategoryId,
  contentfulThemeApiClient: contentfulThemeApiClientProp,
  isAttachSuggestionEnabled = true,
}: UseAttachSuggestionProps): UseAttachSuggestionResult => {
  const { contentfulThemeApiClient: contentfulThemeApiClientContext } = usePublicApiClient();
  const contentfulThemeApiClient = contentfulThemeApiClientProp || contentfulThemeApiClientContext;
  if (!contentfulThemeApiClient) {
    throw new Error(
      'useAttachCategorySuggestions must be provided with a contentfulThemeApiClient via PublicApiClientProvider or props'
    );
  }

  const { isLoading, data: { data } = {} } = useQuery({
    enabled: Boolean(purchasedCategoryId && contentfulThemeApiClient && isAttachSuggestionEnabled),
    queryKey: ['useAttachCategorySuggestions', purchasedCategoryId],
    queryFn: () =>
      contentfulThemeApiClient?.getAttachCategorySuggestions({ purchasedCategoryId: purchasedCategoryId! }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const suggestion = data?.attachSuggestionCollection?.items?.[0];

  const suggestedCategoriesQuery = useContentfulCollections({
    collections: suggestion?.suggestedCategoriesCollection?.items?.map(item => ({ handle: item?.handle })) ?? [],
    contentfulThemeApiClient,
  });

  return {
    isLoading: isLoading,
    attachHeading: suggestion?.attachHeading,
    productBlockHeading: suggestion?.productBlockHeading,
    suggestedCategories: suggestedCategoriesQuery
      ?.map(res => res?.collection)
      .filter(Boolean) as Array<ContentfulCollection>,
  };
};
